/**
 * Auto-track key conversion events site-wide via delegated listeners:
 *   - phone_click       — any <a href="tel:...">
 *   - whatsapp_click    — any <a href*="wa.me"|"api.whatsapp.com"|"chat.whatsapp.com">
 *   - email_click       — any <a href="mailto:...">
 *   - form_submit_attempt — any <form> submit (skips forms marked data-track="off");
 *                         engagement only, never a lead conversion.
 *
 * One document-level listener each; no per-component changes required.
 */
import {
  trackPhoneClick,
  trackWhatsAppClick,
  trackEmailClick,
  trackFormStart,
} from './analytics';
import { trackCtaHref } from './conversionEvents';

let _wired = false;

function findAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (el.tagName === 'A') return el as HTMLAnchorElement;
    el = el.parentElement;
  }
  return null;
}

function isWhatsAppHref(href: string): boolean {
  return /(?:^|\/\/)(?:wa\.me|(?:api|chat)\.whatsapp\.com)\b/i.test(href)
    || href.toLowerCase().startsWith('whatsapp:');
}

export function initAutoEvents(): void {
  if (_wired) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  _wired = true;

  document.addEventListener(
    'click',
    (e) => {
      const a = findAnchor(e.target);
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href) return;
      try {
        if (href.startsWith('tel:')) {
          trackPhoneClick(href.slice(4));
        } else if (href.startsWith('mailto:')) {
          trackEmailClick(href.slice(7).split('?')[0]);
        } else if (isWhatsAppHref(href)) {
          trackWhatsAppClick();
        } else {
          trackCtaHref(href);
        }
      } catch {
        /* never break UX for analytics */
      }
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || form.tagName !== 'FORM') return;
      // Opt-out: forms already reporting via useSecureFormSubmit / BuyerGuide
      if (form.dataset.track === 'off') return;
      // Any form wired to the secure submit hook reports its own conversion
      // after the backend confirms — never double-count it here.
      if (form.closest('[data-track="off"]')) return;
      const name =
        form.dataset.formName ||
        form.getAttribute('name') ||
        form.id ||
        'unnamed_form';
      try {
        // Engagement signal only: a submit event is not a confirmed lead, so
        // this must never fire GA4 generate_lead.
        trackFormStart(name);
      } catch {
        /* ignore */
      }
    },
    { capture: true, passive: true },
  );
}

